import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Colours from '../../design-system/colours';
import Variables from '../../design-system/variables';
import { NotificationContentTabType } from '../../common/model/type/notification-content-tab.type';
import OvPageTitle from '../UI/molecules/OvPageTitle';
import MarkUnreadChatAltIcon from '@mui/icons-material/MarkUnreadChatAlt';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useRouteUrl } from '../../hooks/use-route-url';
import { Alert, AlertTitle, Tab, Tabs } from '@mui/material';
import NotificationContentsList from './NotificationContentsList';
import {
  generalNotificationContentTypes,
  NotificationContentCategory,
} from '../../common/model/dto/notification/notification-content';
import { last } from 'lodash';

const NotificationsContents = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const url = useRouteUrl();
  const [activeTab, setActiveTab] = useState<NotificationContentTabType>(
    NotificationContentTabType.GENERAL
  );

  useEffect(() => {
    const setActiveTabFromUrl = () => {
      const pathName = last(
        location.pathname.split('/')
      ) as NotificationContentTabType;
      const validTabs = Object.values(NotificationContentTabType);

      if (validTabs.includes(pathName)) {
        setActiveTab(pathName);
      } else {
        setActiveTab(NotificationContentTabType.GENERAL);
      }
    };

    setActiveTabFromUrl();
  }, [location.pathname]);

  const changeTab = (event: any, newValue: NotificationContentTabType) => {
    setActiveTab(newValue);
  };

  return (
    <Container>
      <OvPageTitle
        title={t('app.subPages.menu.notificationContents')}
        icon={<MarkUnreadChatAltIcon />}
      />

      {activeTab === NotificationContentTabType.GENERAL && (
        <StyledAlert severity="info">
          <AlertTitle>
            {t('notificationContents.infoBox.generalTitle')}
          </AlertTitle>
          {t('notificationContents.infoBox.generalDescription')}
        </StyledAlert>
      )}

      {activeTab === NotificationContentTabType.INSIGHTS && (
        <StyledAlert severity="info">
          <AlertTitle>
            {t('notificationContents.infoBox.insightsTitle')}
          </AlertTitle>
          {t('notificationContents.infoBox.insightsDescription')}
        </StyledAlert>
      )}

      {activeTab === NotificationContentTabType.REGISTRATION && (
        <StyledAlert severity="info">
          <AlertTitle>
            {t('notificationContents.infoBox.registrationTitle')}
          </AlertTitle>
          {t('notificationContents.infoBox.registrationDescription')}
        </StyledAlert>
      )}

      {activeTab === NotificationContentTabType.SYMPTOMS && (
        <StyledAlert severity="info">
          <AlertTitle>
            {t('notificationContents.infoBox.symptomTitle')}
          </AlertTitle>
          {t('notificationContents.infoBox.symptomDescription')}
        </StyledAlert>
      )}

      <ContentWrapper>
        <TabsContainer>
          <Tabs value={activeTab} onChange={changeTab}>
            <Tab
              value={NotificationContentTabType.GENERAL}
              label={t('notificationContents.tabs.general')}
              component={Link}
              to={`${url}/${NotificationContentTabType.GENERAL}`}
              style={{
                borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                textTransform: 'none',
              }}
            />
            <Tab
              value={NotificationContentTabType.INSIGHTS}
              label={t('notificationContents.tabs.insights')}
              component={Link}
              to={`${url}/${NotificationContentTabType.INSIGHTS}`}
              style={{
                borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                textTransform: 'none',
              }}
            />
            <Tab
              value={NotificationContentTabType.REGISTRATION}
              label={t('notificationContents.tabs.registration')}
              component={Link}
              to={`${url}/${NotificationContentTabType.REGISTRATION}`}
              style={{
                borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                textTransform: 'none',
              }}
            />
            <Tab
              value={NotificationContentTabType.SYMPTOMS}
              label={t('notificationContents.tabs.symptoms')}
              component={Link}
              to={`${url}/${NotificationContentTabType.SYMPTOMS}`}
              style={{
                borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                textTransform: 'none',
              }}
            />
          </Tabs>
        </TabsContainer>

        <Routes>
          <Route
            index
            element={
              <Navigate
                to={`${url}/${NotificationContentTabType.GENERAL}`}
                replace
              />
            }
          />
          <Route
            path={`${NotificationContentTabType.GENERAL}/*`}
            element={
              <NotificationContentsList
                key={'general'}
                useAxiosParams={{
                  categories: generalNotificationContentTypes,
                }}
                tabType={NotificationContentTabType.GENERAL}
              />
            }
          />
          <Route
            path={`${NotificationContentTabType.INSIGHTS}/*`}
            element={
              <NotificationContentsList
                key={'insights'}
                useAxiosParams={{
                  category: NotificationContentCategory.INSIGHTS,
                }}
                tabType={NotificationContentTabType.INSIGHTS}
              />
            }
          />
          <Route
            path={`${NotificationContentTabType.REGISTRATION}/*`}
            element={
              <NotificationContentsList
                key={'registration'}
                useAxiosParams={{
                  event_type: 'user.registered',
                }}
                tabType={NotificationContentTabType.REGISTRATION}
              />
            }
          />
          <Route
            path={`${NotificationContentTabType.SYMPTOMS}/*`}
            element={
              <NotificationContentsList
                key={'symptom'}
                useAxiosParams={{
                  event_type: 'user.symptom-logged',
                }}
                tabType={NotificationContentTabType.SYMPTOMS}
              />
            }
          />
        </Routes>
      </ContentWrapper>
    </Container>
  );
};

export default NotificationsContents;

const Container = styled.div`
  margin-left: 1rem;
  box-shadow: ${Variables.boxShadow.defaultBox};
  background-color: ${Colours.WHITE};
  padding: 1.5rem;
  border-radius: ${Variables.borderRadius.LARGE};
`;

const ContentWrapper = styled.div`
  margin-top: 1.5rem;
`;

const TabsContainer = styled.div`
  margin-bottom: 1.5rem;

  && .MuiTabs-indicator {
    background-color: ${Colours.OV_BASE};
  }
`;

const StyledAlert = styled(Alert)`
  && {
    top: 4.25rem;
    margin: 1rem 0;
    border-radius: ${Variables.borderRadius.LARGE};
    background-color: ${Colours.OV_BORDER_COLOR_V3};
    color: ${Colours.OV_BASE};

    .MuiAlertTitle-root {
      font-weight: bold !important;
    }
  }
`;
